import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GraduationCap, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex" data-testid="login-page">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <Link to="/" className="flex items-center gap-2 mb-8">
              <div className="w-10 h-10 bg-[#002FA7] rounded-sm flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-black tracking-tight text-[#0A0A0A]">RIKIRTUTOR</span>
            </Link>
            <h1 className="text-3xl font-bold text-[#0A0A0A] mb-2">Welcome back</h1>
            <p className="text-[#4B5563]">Log in to continue your learning journey</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-[#E53935]/10 border border-[#E53935]/20 text-[#E53935] px-4 py-3 rounded-sm text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-[#0A0A0A] font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-2 rounded-sm border-[#E5E7EB] focus:ring-[#002FA7]"
                required
                data-testid="login-email"
              />
            </div>

            <div>
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-[#0A0A0A] font-medium">Password</Label>
                <Link to="/forgot-password" className="text-sm text-[#002FA7] hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="rounded-sm border-[#E5E7EB] focus:ring-[#002FA7] pr-10"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#002FA7] hover:bg-[#001B63] text-white font-bold py-6 rounded-sm"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Logging in...' : 'Log In'}
            </Button>
          </form>

          <p className="mt-8 text-center text-[#4B5563]">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#002FA7] font-medium hover:underline" data-testid="signup-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <img
          src="https://images.pexels.com/photos/12064/pexels-photo-12064.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Library"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#002FA7]/60"></div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white max-w-md">
            <h2 className="text-3xl font-bold mb-4">Unlock Your Potential</h2>
            <p className="text-white/80">
              Join thousands of students achieving their academic goals with personalized tutoring.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
